<script setup>
const props = defineProps({
    headline: { type: String, default: '' },
    copy: { type: String, default: '' },
    stages: { type: Array, default: () => [] },
});

// Starts collapsed (-1), not on row 0. QA (UNB): opening the first row by default
// pushed the rest of the block far down the mobile page. Desktop is unaffected —
// desktopIndex below floors this to 0, so the tablist still opens on stage one.
const activeIndex = ref(-1);

// Mobile can collapse to -1; the desktop tablist must always have a selection.
const desktopIndex = computed(() => (activeIndex.value < 0 ? 0 : activeIndex.value));

// One source of truth for both layouts: the desktop detail pane and the mobile
// accordion read the same index (via desktopIndex), so they can never disagree.
const activeStage = computed(() => props.stages?.[desktopIndex.value] ?? null);

const select = (index) => { activeIndex.value = index; };
// Mobile accordion: tapping the open row collapses it.
const toggle = (index) => { activeIndex.value = activeIndex.value === index ? -1 : index; };

// Instance-unique prefix so multiple Stages blocks on one page never collide
// on DOM ids (mobile accordion panels + desktop tab/tabpanel pairs).
const uid = useId();
const tabId = (index) => `stage-tab-${uid}-${index}`;
const panelId = () => `stage-panel-${uid}`;
const mobilePanelId = (index) => `stage-panel-${uid}-${index}`;
const mobileTriggerId = (index) => `stage-trigger-${uid}-${index}`;

const tabRefs = ref([]);
const setTabRef = (el, index) => { tabRefs.value[index] = el; };

const focusTab = (index) => {
    const el = tabRefs.value[index];
    if (el?.focus) el.focus();
};

// Vertical roving-tabindex arrow-key navigation for the desktop tablist.
// Moving with arrows both selects the tab and moves focus to it, wrapping
// around at the ends.
const onTabKeydown = (event) => {
    const count = props.stages?.length ?? 0;
    if (!count) return;
    let nextIndex = null;
    if (event.key === 'ArrowDown') {
        nextIndex = (desktopIndex.value + 1) % count;
    } else if (event.key === 'ArrowUp') {
        nextIndex = (desktopIndex.value - 1 + count) % count;
    } else if (event.key === 'Home') {
        nextIndex = 0;
    } else if (event.key === 'End') {
        nextIndex = count - 1;
    } else {
        return;
    }
    event.preventDefault();
    select(nextIndex);
    focusTab(nextIndex);
};

// Purely decorative background layers (desktop-only, from Figma). Referenced
// by public path — never `import` media, it blows the Worker memory limit.
const ellipseBottomLeft = '/img/2026/stages/stages-bottom-left-ellipse.svg';
// @2x export: it renders at 514px CSS wide on a 1440 frame, so the 1x asset was
// soft on retina. Same artwork and same crop — 1048x500 is exactly twice the 1x.
const shardTopRight = '/img/2026/stages/stages-top-right-ellipse@2x.png';
</script>

<template>
    <section
        v-if="stages?.length"
        data-test="stages-block"
        class="block-stages relative isolate overflow-hidden text-white px-[20px] py-[60px] lg:px-[40px] lg:py-[90px]"
        style="background-image: linear-gradient(206.72deg, #d63d00 8.44%, #d63d00 49.70%, #820ca7 88.50%)">

        <div class="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            <!-- Top-right shard (UNB-671). Figma clips it to a 514x231 frame flush
                 in the block's top-right corner (node 5808:149425), so the artwork
                 is exported already cropped to that corner — placed at its own
                 proportions, no rotation and no object-fit, both of which would
                 re-crop it. 35.7% is 514/1440 from the Figma frame, so it holds the
                 same corner relationship as the viewport widens.
                 Sits UNDER the glow to match Figma's layer order, where the amber
                 ellipse washes over the shard rather than sitting behind it. -->
            <img :src="shardTopRight" alt="" loading="lazy" decoding="async"
                class="hidden lg:display-block absolute right-0 top-0 w-[35.7%] h-auto" />
            <!-- Bright-amber corner glow (UNB-671): per Figma's "Stages Block (No
                 Images)" reference, the glow now sits ABOVE the block — overflowing
                 past the top-right corner via negative offsets — rather than flush
                 inside it. Figma spec at the 1440px frame: a 353px circle, blurred
                 60px, positioned right:-96px / top:-223px; scaled proportionally here
                 so it holds the same corner relationship at other viewport widths.
                 The vertical offset is in vw, NOT %: a percentage `top` resolves
                 against the container's HEIGHT, so -15.5% landed the glow at -115px
                 instead of -223px and left it hanging ~108px too low into the block.
                 Width and `right` are width-relative already, but they're in the same
                 unit here so the three stay locked together. This block is full-bleed,
                 so 1vw is 1% of its width. Desktop-only, like the rest of this corner
                 dressing — it would crowd the mobile accordion. -->
            <div class="hidden lg:display-block absolute -top-[15.49vw] -right-[6.67vw]
                w-[24.5vw] aspect-square rounded-full bg-(--bright-amber) blur-[60px]" />
            <!-- Bottom-left pair stays desktop-only — they'd crowd the accordion. -->
            <img :src="ellipseBottomLeft" alt="" loading="lazy" decoding="async"
                class="hidden lg:display-block absolute w-[267px] h-[267px] left-[-30px] bottom-[-80px]" />
        </div>

        <div class="relative z-[5] flex flex-col gap-[40px] lg:gap-[55px]">

            <!-- Header -->
            <div class="flex flex-col gap-[18px] items-start max-w-[899px]">
                <h2 v-if="headline"
                    class="font-hubspot-serif font-normal text-[36px] leading-[1.1] lg:text-[60px] m-0">
                    {{ headline }}
                </h2>
                <div v-if="copy"
                    class="stages-rich font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] [&_p]:m-0"
                    v-html="copy"></div>
            </div>

            <!-- Desktop: pill list + detail pane -->
            <!-- Left-aligned with a fixed 135px gutter (Figma: pill column ends at
                 440, detail starts at 575). Deliberately NOT justify-between on a
                 full-width row — that flings the detail pane to the far edge of a
                 wide screen instead of keeping it beside the list. -->
            <div class="hidden lg:flex items-start gap-[135px]">
                <div role="tablist" aria-label="Stages" aria-orientation="vertical"
                    class="flex flex-col gap-[18px] w-[440px] shrink-0" @keydown="onTabKeydown">
                    <button
                        v-for="(stage, index) in stages"
                        :ref="(el) => setTabRef(el, index)"
                        :key="index"
                        :id="tabId(index)"
                        data-test="stage-pill"
                        role="tab"
                        type="button"
                        :aria-selected="desktopIndex === index"
                        :aria-controls="panelId()"
                        :tabindex="desktopIndex === index ? 0 : -1"
                        :class="[
                            'group flex items-center px-[20px] py-[10px] rounded-[50px] w-full cursor-pointer',
                            'font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] text-left',
                            'transition-colors duration-200',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                            stage.badge ? 'gap-[16px]' : 'gap-[10px]',
                            desktopIndex === index
                                ? 'bg-white text-(--magenta) border border-white'
                                // Hover borrows the selected treatment, per QA. The
                                // pills had no hover at all, so nothing said they were
                                // clickable until you hit one.
                                : 'bg-transparent text-white border border-white hover:bg-white hover:text-(--magenta)',
                        ]"
                        @click="select(index)">
                        <span>{{ stage.name }}</span>
                        <span v-if="stage.badge"
                            data-test="stage-badge"
                            :class="[
                                'font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase',
                                'transition-colors duration-200',
                                // The badge has to move with the pill: hover fills the
                                // pill white, and a white badge on white is invisible.
                                desktopIndex === index ? 'text-(--dark-pink)' : 'text-white group-hover:text-(--dark-pink)',
                            ]">
                            {{ stage.badge }}
                        </span>
                    </button>
                </div>

                <!-- flex-1, not a fixed 785px: the pane fills whatever is left of the
                     row, so the image grows with the viewport instead of stranding
                     empty space. At the 1440 Figma width this resolves to exactly
                     785px (1440 - 80 padding - 440 pills - 135 gutter). -->
                <div v-if="activeStage" :id="panelId()" role="tabpanel" :aria-labelledby="tabId(desktopIndex)"
                    class="flex flex-col gap-[49px] flex-1 min-w-0">
                    <!-- In flow at the image's own aspect ratio (UNB-631): the old fixed
                         785/537 crop box cut images off wherever they didn't match it,
                         so the full image now shows — width-driven, intrinsic height. -->
                    <img
                        v-if="activeStage.image?.src"
                        data-test="stage-image"
                        :src="activeStage.image.src"
                        :alt="activeStage.image.alt || activeStage.image.altText || activeStage.title || ''"
                        class="w-full h-auto rounded-[10px]" />
                    <div class="flex flex-col gap-[17px] items-start">
                        <h3 data-test="stage-detail-title"
                            class="font-hubspot-serif font-normal text-[36px] leading-[1.2] m-0">
                            {{ activeStage.title }}
                        </h3>
                        <div v-if="activeStage.description"
                            class="stages-rich font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] [&_p]:m-0"
                            v-html="activeStage.description"></div>
                    </div>
                </div>
            </div>

            <!-- Mobile: accordion in the "Experience UNBOUND" treatment (faq.vue —
                 registered in Builder as "Festival Accordion", NOT the orphaned
                 festivalAccordion.vue). QA (UNB) called the old mobile layout
                 disorienting on three counts: the pills read as site buttons rather
                 than accordion rows, row 0 opened by default and shoved everything
                 down, and each pill's label was repeated verbatim underneath by the
                 panel's own <h3>. Serif label + circled +/- glyph on a hairline rule
                 reads as an accordion; the duplicate <h3> is dropped below (see the
                 panel). Desktop keeps its pills — this ticket is mobile-only. -->
            <div class="flex flex-col gap-[16px] lg:hidden">
                <div v-for="(stage, index) in stages" :key="index"
                    class="border-b border-white/50"
                    :class="activeIndex === index ? 'pb-[30px]' : 'pb-[18px]'">
                    <button
                        type="button"
                        :id="mobileTriggerId(index)"
                        data-test="stage-accordion-trigger"
                        :aria-expanded="activeIndex === index"
                        :aria-controls="mobilePanelId(index)"
                        class="group flex items-center justify-between w-full gap-[30px] text-left
                            cursor-pointer bg-transparent border-none p-0
                            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white
                            focus-visible:ring-offset-4 focus-visible:ring-offset-transparent rounded-sm"
                        @click="toggle(index)">
                        <span class="flex items-baseline gap-[10px] min-w-0">
                            <span class="font-hubspot-serif text-white text-[26px] leading-[1.1]">{{ stage.name }}</span>
                            <!-- Always white now: there is no filled pill behind it to
                                 contrast against, so the old active --dark-pink would
                                 read as a stray colour on the gradient. -->
                            <span v-if="stage.badge"
                                data-test="stage-badge-mobile"
                                class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-white shrink-0">
                                {{ stage.badge }}
                            </span>
                        </span>
                        <span aria-hidden="true" class="flex-none">
                            <svg viewBox="0 0 37 37" class="w-[37px] h-[37px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="18.5" cy="18.5" r="17.5" stroke-width="1"
                                    class="stroke-white fill-transparent transition-[fill] duration-200 group-hover:fill-white" />
                                <line x1="12.75" y1="18.5" x2="24.25" y2="18.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                    class="stroke-white transition-[stroke] duration-200 group-hover:stroke-(--orange)" />
                                <!-- The vertical stroke drops away on open, turning + into - -->
                                <line x1="18.5" y1="12.75" x2="18.5" y2="24.25" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                    :class="activeIndex === index && 'opacity-0'"
                                    class="stroke-white transition-[opacity,stroke] duration-300 group-hover:stroke-(--orange)" />
                            </svg>
                        </span>
                    </button>

                    <!-- Panel stays mounted so it can animate open/closed (the site's
                         grid-template-rows 0fr->1fr trick, as in faq.vue). Keeping it
                         mounted also means `aria-controls` above always points at a real
                         element, which it didn't when the panel was v-if'd away.

                         The <img> is mounted ONLY for the open panel. `loading="lazy"`
                         does NOT save you here — a collapsed panel is zero-height but
                         still intersects the viewport, so the browser happily fetches
                         all nine stage images (measured). The panel's open animation
                         settles at natural height, then adjusts once the image loads
                         (no reserved box — intrinsic ratios vary per image, UNB-631). -->
                    <div :id="mobilePanelId(index)" role="region" :aria-labelledby="mobileTriggerId(index)"
                        class="stage-panel" :class="{ 'is-open': activeIndex === index }">
                        <div class="overflow-hidden">
                            <!-- The row itself now carries the bottom padding and the
                                 hairline rule, so the panel only needs its top offset.
                                 No <h3> here: it restated the trigger word for word
                                 (verified on staging — stage.name === stage.title for
                                 every row), which is the "stacked twice" QA flagged.
                                 The trigger is the accessible name via aria-labelledby,
                                 so nothing is lost for screen readers. Desktop keeps
                                 its own <h3>, where the pill sits in a separate column
                                 and the repetition doesn't happen. -->
                            <div class="flex flex-col gap-[24px] pt-[12px]">
                                <img
                                    v-if="stage.image?.src && activeIndex === index"
                                    :src="stage.image.src"
                                    :alt="stage.image.alt || stage.image.altText || stage.title || ''"
                                    decoding="async"
                                    class="w-full h-auto rounded-[10px]" />
                                <div v-if="stage.description"
                                    class="stages-rich font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] [&_p]:m-0"
                                    v-html="stage.description"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
    /* Inline text links inside Builder rich text — the block's copy and each
       stage's description. They arrive from the CMS with NO classes, so they
       rendered as plain white text with no underline at all, indistinguishable
       from the prose around them (UNB). These are the standard inline-link values
       QA specified.

       `:deep()` is required, not optional: scoped styles are applied via a data
       attribute stamped at compile time, and v-html content is injected at
       runtime, so it never gets that attribute. A plain `.stages-rich a` rule
       here would match nothing. */
    .stages-rich :deep(a) {
        text-decoration-line: underline;
        text-decoration-style: dotted;
        text-decoration-skip-ink: auto;
        text-decoration-color: #fff;
        text-decoration-thickness: 8.5%;
        text-underline-offset: 25%;
    }

    /* Decoration goes on hover; the colour deliberately does NOT change. QA asked
       for that explicitly on accessibility grounds — white on this gradient is the
       contrast that passes, and dimming it on hover would undo that. */
    .stages-rich :deep(a:hover) {
        text-decoration-line: none;
    }

    /* Stage images are plain 10px-rounded rectangles (UNB-612) — the shard clip
       was removed per Shannon; the rounding lives on the img via rounded-[10px]. */

    /* Mobile accordion. The grid 0fr -> 1fr trick animates to the panel's natural
       height without hardcoding a max-height (which either clips tall content or
       makes short content ease sluggishly). Same technique as faq.vue. */
    .stage-panel {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 400ms ease;
    }

    .stage-panel.is-open {
        grid-template-rows: 1fr;
    }

    @media (prefers-reduced-motion: reduce) {
        .stage-panel {
            transition: none;
        }
    }
</style>
